import React from 'react';
import { Box, Typography, Avatar } from '@mui/material';

/* ─── Content renderer — parses - / • as bullet lists ───────────────────── */
export function renderContent(text) {
  if (!text) return null;
  const parts = [];
  let bullets = [];
  const flushBullets = () => {
    if (bullets.length) { parts.push({ type: 'bullets', items: [...bullets] }); bullets = []; }
  };
  text.split('\n').forEach(line => {
    const m = line.match(/^[-•]\s+(.*)/);
    if (m) { bullets.push(m[1]); }
    else { flushBullets(); parts.push({ type: 'line', text: line }); }
  });
  flushBullets();
  return (
    <Box>
      {parts.map((p, i) => {
        if (p.type === 'bullets') return (
          <Box key={i} component="ul" sx={{ m: 0, pl: 3, mb: 0.3 }}>
            {p.items.map((item, j) => (
              <Box key={j} component="li" sx={{ fontSize: '9pt', color: '#222', lineHeight: 1.4 }}>{item}</Box>
            ))}
          </Box>
        );
        if (!p.text.trim()) return <Box key={i} sx={{ height: '0.45em' }} />;
        return <Typography key={i} sx={{ fontSize: '9pt', color: '#222', lineHeight: 1.4 }}>{p.text}</Typography>;
      })}
    </Box>
  );
}

/* ─── Pricing preview ────────────────────────────────────────────────────── */
export function PricingPreview({ section }) {
  const isHourly = section.pricingMode !== 'fixed';
  const rows = section.rows || [];
  return (
    <Box>
      <Typography sx={{ fontSize: '9pt', color: '#222', mb: 1 }}>
        {isHourly
          ? 'Ersättning enligt löpande räkning mot budget:'
          : 'Vi erbjuder oss att utföra uppdraget mot fast pris:'}
      </Typography>
      <Box sx={{ mb: 1 }}>
        {rows.map(row => row.description && (
          <Box key={row.id} sx={{
            display: 'flex', justifyContent: 'space-between',
            borderBottom: '1px solid #eee', py: 0.4,
          }}>
            <Typography sx={{ fontSize: '9pt', color: '#222', textDecoration: 'underline', textUnderlineOffset: 2 }}>
              {row.description}
            </Typography>
            <Typography sx={{ fontSize: '9pt', fontWeight: 600, color: '#222' }}>
              {row.price ? `${Number(row.price).toLocaleString('sv-SE')} SEK${isHourly ? '/tim' : ''}` : '–'}
            </Typography>
          </Box>
        ))}
      </Box>
      {isHourly && section.budgetNote && (
        <Box sx={{ mb: 1 }}>
          <Typography sx={{ fontSize: '10pt', fontWeight: 700, color: '#222' }}>Budget:</Typography>
          <Typography sx={{ fontSize: '9pt', color: '#222' }}>{section.budgetNote}</Typography>
        </Box>
      )}
      {section.paymentTerms && (
        <Typography sx={{ fontSize: '9pt', color: '#222', mb: 1, lineHeight: 1.4 }}>
          {section.paymentTerms}
        </Typography>
      )}
      {section.content && (
        <Box sx={{ mt: 0.5 }}>
          <Typography sx={{ fontSize: '10pt', fontWeight: 700, color: '#222' }}>Genomförandebeskrivning</Typography>
          <Typography sx={{ fontSize: '9pt', color: '#222', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
            {section.content}
          </Typography>
        </Box>
      )}
    </Box>
  );
}

/* ─── Budget preview ─────────────────────────────────────────────────────── */
function generateMonths(start, end) {
  if (!start || !end) return [];
  const months = [];
  let [sy, sm] = start.split('-').map(Number);
  const [ey, em] = end.split('-').map(Number);
  while ((sy < ey || (sy === ey && sm <= em)) && months.length < 36) {
    months.push(`${sy}-${String(sm).padStart(2, '0')}`);
    sm++; if (sm > 12) { sm = 1; sy++; }
  }
  return months;
}

function fmtSEK(val) {
  return Number(val).toLocaleString('sv-SE') + ' kr';
}

function fmtMonthShort(ym) {
  const names = ['jan','feb','mar','apr','maj','jun','jul','aug','sep','okt','nov','dec'];
  const [, m] = ym.split('-');
  return names[parseInt(m, 10) - 1];
}

function BudgetTable({ cols, rows, footerRow, emptyText }) {
  return (
    <Box sx={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }} component="table">
      <Box component="thead">
        <Box component="tr" sx={{ borderBottom: '1.5px solid #1a1a1a' }}>
          {cols.map((col, ci) => {
            const w = col.w === 'auto' ? undefined : col.w;
            return (
              <Box key={col.label} component="th"
                sx={{
                  fontSize: '7.5pt', fontWeight: 700, color: '#1a1a1a',
                  textAlign: col.align || 'left', pb: 0.5,
                  pl: col.separator ? 2 : undefined,
                  pr: col.align === 'left' ? 1 : 0,
                  width: w, minWidth: w,
                  whiteSpace: 'nowrap',
                  letterSpacing: 0.3,
                  ...(col.separator && { borderLeft: '2px solid #e0e0e0' }),
                }}>
                {col.label.toUpperCase()}
              </Box>
            );
          })}
        </Box>
      </Box>
      <Box component="tbody">
        {rows.length === 0 ? (
          <Box component="tr">
            <Box component="td" colSpan={cols.length}
              sx={{ fontSize: '8.5pt', color: '#aaa', fontStyle: 'italic', py: 1 }}>
              {emptyText || '—'}
            </Box>
          </Box>
        ) : rows.map((row, i) => (
          <Box key={row.key || i} component="tr"
            sx={{ borderBottom: '1px solid #eee', bgcolor: i % 2 === 0 ? 'transparent' : '#fafafa' }}>
            {row.cells.map((cell, ci) => (
              <Box key={ci} component="td"
                sx={{
                  fontSize: '9pt', py: 0.5,
                  pl: cols[ci]?.separator ? 2 : undefined,
                  pr: cols[ci]?.align === 'left' ? 1 : 0,
                  textAlign: cols[ci]?.align || 'left',
                  ...(cols[ci]?.separator && { borderLeft: '2px solid #e0e0e0' }),
                  ...cell.sx,
                }}>
                {cell.content}
              </Box>
            ))}
          </Box>
        ))}
      </Box>
      {footerRow && (
        <Box component="tfoot">
          <Box component="tr" sx={{ borderTop: '1.5px solid #1a1a1a' }}>
            {footerRow.map((cell, ci) => (
              <Box key={ci} component="td"
                sx={{
                  fontSize: '9pt', fontWeight: 700, pt: 0.5,
                  pl: cols[ci]?.separator ? 2 : undefined,
                  pr: cols[ci]?.align === 'left' ? 1 : 0,
                  textAlign: cols[ci]?.align || 'left',
                  ...(cols[ci]?.separator && { borderLeft: '2px solid #e0e0e0' }),
                  ...cell.sx,
                }}>
                {cell.content}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

export function BudgetPreview({ section, budgets }) {
  const budget = (budgets || []).find(b => b.id === section.budgetId);
  if (!budget) return (
    <Box sx={{ border: '1px dashed #ddd', borderRadius: 1, p: 1.5 }}>
      <Typography sx={{ fontSize: '8.5pt', color: '#aaa', fontStyle: 'italic' }}>
        {(budgets || []).length === 0
          ? 'Ingen budget skapad ännu.'
          : 'Ingen budgetversion vald. Välj i editorns inställningar.'}
      </Typography>
    </Box>
  );

  const resources = budget.resources || [];
  const activities = budget.activities || [];
  const months = generateMonths(budget.startMonth || '', budget.endMonth || '');
  const displayMode = section.budgetDisplay || 'activity';

  const periodStr = budget.startMonth && budget.endMonth
    ? `${budget.startMonth} – ${budget.endMonth}`
    : budget.startMonth || budget.endMonth || '';

  function resLabel(res) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {(res.isRole || !res.userId) && (
          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#9C27B0', flexShrink: 0, mt: '1px' }} />
        )}
        {res.name || '—'}
      </Box>
    );
  }

  // ── Mode: per-activity ───────────────────────────────────────────────────
  if (displayMode === 'activity') {
    const rows = activities.map(act => {
      const res = resources.find(r => r.id === act.resourceId);
      const hrs = months.reduce((s, m) => s + (parseFloat(act.months?.[m]) || 0), 0);
      const rate = parseFloat(res?.hourlyRate) || 0;
      return { key: act.id, act, res, hrs, rate, val: hrs * rate };
    }).filter(r => r.hrs > 0);
    const grandHrs = rows.reduce((s, r) => s + r.hrs, 0);
    const grandVal = rows.reduce((s, r) => s + r.val, 0);
    const cols = [
      { label: 'Aktivitet', align: 'left', w: '30%' },
      { label: 'Roll / Person', align: 'left', w: '28%' },
      { label: 'Timmar', align: 'right', w: '14%' },
      { label: 'Timpris', align: 'right', w: '14%' },
      { label: 'Summa', align: 'right', w: '14%' },
    ];
    return (
      <Box>
        {periodStr && <Typography sx={{ fontSize: '8pt', color: '#888', mb: 1 }}>Period: {periodStr}</Typography>}
        <BudgetTable
          cols={cols}
          rows={rows.map(r => ({
            key: r.key,
            cells: [
              { content: r.act.name || <em style={{ color: '#bbb' }}>—</em> },
              { content: r.res ? resLabel(r.res) : <em style={{ color: '#bbb' }}>—</em> },
              { content: `${r.hrs.toFixed(0)} tim` },
              { content: r.rate ? Number(r.rate).toLocaleString('sv-SE') : '–', sx: { color: '#555' } },
              { content: r.val > 0 ? fmtSEK(r.val) : '–', sx: { fontWeight: 600, color: '#1B5E20' } },
            ],
          }))}
          footerRow={grandHrs > 0 ? [
            { content: 'Totalt' },
            { content: '' },
            { content: `${grandHrs.toFixed(0)} tim` },
            { content: '' },
            { content: fmtSEK(grandVal), sx: { color: '#FF6B00' } },
          ] : null}
          emptyText="Inga aktiviteter med timmar i denna budget."
        />
      </Box>
    );
  }

  // ── Mode: roll × månad ───────────────────────────────────────────────────
  if (displayMode === 'role-month') {
    // One row per resource, one column per month + total
    const resRows = resources.map(res => {
      const byMonth = {};
      months.forEach(m => {
        byMonth[m] = activities
          .filter(a => a.resourceId === res.id)
          .reduce((s, a) => s + (parseFloat(a.months?.[m]) || 0), 0);
      });
      const totalHrs = months.reduce((s, m) => s + byMonth[m], 0);
      const totalVal = totalHrs * (parseFloat(res.hourlyRate) || 0);
      return { key: res.id, res, byMonth, totalHrs, totalVal };
    }).filter(r => r.totalHrs > 0);

    const grandHrs = resRows.reduce((s, r) => s + r.totalHrs, 0);
    const grandVal = resRows.reduce((s, r) => s + r.totalVal, 0);

    const monthCols = months.map(m => ({ label: fmtMonthShort(m), align: 'right', w: 28, m }));
    const cols = [
      { label: 'Roll / Person', align: 'left', w: 'auto' },
      ...monthCols,
      { label: 'Tim', align: 'right', w: 52, separator: true },
      { label: 'Summa', align: 'right', w: 90 },
    ];

    return (
      <Box>
        {periodStr && <Typography sx={{ fontSize: '8pt', color: '#888', mb: 1 }}>Period: {periodStr}</Typography>}
        <Box sx={{ overflowX: 'auto' }}>
          <BudgetTable
            cols={cols}
            rows={resRows.map(r => ({
              key: r.key,
              cells: [
                { content: resLabel(r.res) },
                ...months.map(m => ({
                  content: r.byMonth[m] > 0 ? r.byMonth[m].toFixed(0) : '',
                  sx: { color: '#555', fontSize: '8.5pt' },
                })),
                { content: `${r.totalHrs.toFixed(0)} tim`, sx: { fontWeight: 600, whiteSpace: 'nowrap' } },
                { content: r.totalVal > 0 ? fmtSEK(r.totalVal) : '–', sx: { fontWeight: 600, color: '#1B5E20', whiteSpace: 'nowrap' } },
              ],
            }))}
            footerRow={grandHrs > 0 ? [
              { content: 'Totalt' },
              ...months.map(m => ({
                content: resRows.reduce((s, r) => s + r.byMonth[m], 0) > 0
                  ? resRows.reduce((s, r) => s + r.byMonth[m], 0).toFixed(0)
                  : '',
              })),
              { content: `${grandHrs.toFixed(0)} tim`, sx: { whiteSpace: 'nowrap' } },
              { content: fmtSEK(grandVal), sx: { color: '#FF6B00', whiteSpace: 'nowrap' } },
            ] : null}
            emptyText="Inga resurser med timmar i denna budget."
          />
        </Box>
      </Box>
    );
  }

  // ── Mode: roll totalt (default) ──────────────────────────────────────────
  const resRows = resources.map(res => {
    const totalHrs = activities
      .filter(a => a.resourceId === res.id)
      .reduce((s, a) => s + months.reduce((ms, m) => ms + (parseFloat(a.months?.[m]) || 0), 0), 0);
    const rate = parseFloat(res.hourlyRate) || 0;
    return { key: res.id, res, totalHrs, rate, totalVal: totalHrs * rate };
  }).filter(r => r.totalHrs > 0 || r.rate);
  const grandHrs = resRows.reduce((s, r) => s + r.totalHrs, 0);
  const grandVal = resRows.reduce((s, r) => s + r.totalVal, 0);
  const cols = [
    { label: 'Roll / Person', align: 'left', w: '40%' },
    { label: 'Timpris', align: 'right', w: '20%' },
    { label: 'Timmar', align: 'right', w: '20%' },
    { label: 'Summa', align: 'right', w: '20%' },
  ];
  return (
    <Box>
      {periodStr && <Typography sx={{ fontSize: '8pt', color: '#888', mb: 1 }}>Period: {periodStr}</Typography>}
      <BudgetTable
        cols={cols}
        rows={resRows.map(r => ({
          key: r.key,
          cells: [
            { content: resLabel(r.res) },
            { content: r.rate ? `${Number(r.rate).toLocaleString('sv-SE')} kr/tim` : '–', sx: { color: '#555' } },
            { content: r.totalHrs > 0 ? `${r.totalHrs.toFixed(0)} tim` : '–' },
            { content: r.totalVal > 0 ? fmtSEK(r.totalVal) : '–', sx: { fontWeight: 600, color: '#1B5E20' } },
          ],
        }))}
        footerRow={grandHrs > 0 ? [
          { content: 'Totalt' },
          { content: '' },
          { content: `${grandHrs.toFixed(0)} tim` },
          { content: fmtSEK(grandVal), sx: { color: '#FF6B00' } },
        ] : null}
        emptyText="Inga resurser med timmar i denna budget."
      />
    </Box>
  );
}

/* ─── Team preview ───────────────────────────────────────────────────────── */
export function TeamPreview({ section, users }) {
  const teamUsers = users.filter(u => (section.userIds || []).includes(u.id));
  return (
    <Box>
      {teamUsers.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: section.content ? 1.5 : 0 }}>
          {teamUsers.map(u => (
            <Box key={u.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 28, height: 28, bgcolor: '#1a1a1a', fontSize: '8.5pt' }}>
                {(u.displayName || u.email || '?')[0].toUpperCase()}
              </Avatar>
              <Box>
                <Typography sx={{ fontSize: '9pt', fontWeight: 600 }}>{u.displayName || u.email}</Typography>
                {u.title && <Typography sx={{ fontSize: '8.5pt', color: '#777' }}>{u.title}</Typography>}
              </Box>
            </Box>
          ))}
        </Box>
      )}
      {section.content && (
        <Typography sx={{ fontSize: '9pt', color: '#222', whiteSpace: 'pre-wrap', lineHeight: 1.4 }}>
          {section.content}
        </Typography>
      )}
    </Box>
  );
}

/* ─── Offer preview — used by OfferEditor (screen + print) and PublicOfferView (print) ── */
export default function OfferPreview({
  meta, sections, company, users, logos = {},
  printMode, companyRefs = [], selectedRefIds = [], budgets = [],
}) {
  const today = new Date().toLocaleDateString('sv-SE');

  const pageStyle = printMode ? {
    fontFamily: '"Segoe UI", Arial, sans-serif',
    width: '100%',
    minHeight: '297mm',
    fontSize: '9pt',
    display: 'flex',
    flexDirection: 'column',
  } : {
    bgcolor: 'white',
    width: 794,
    minHeight: 1123,
    mx: 'auto',
    boxShadow: 4,
    fontFamily: '"Segoe UI", Arial, sans-serif',
    fontSize: '9pt',
  };

  return (
    <Box sx={pageStyle}>

      {/* Two-column: narrow left sidebar + wide right content */}
      {/* Screen: gradient on container so gray tracks content height */}
      {/* Print: sidebar bgcolor + minHeight fills the left column on every page */}
      <Box sx={{
        display: 'flex', width: '100%',
        ...(!printMode && { background: 'linear-gradient(to right, #f0f0f0 216px, #ffffff 216px)' }),
        ...(printMode && { minHeight: '297mm' }),
      }}>

        {/* ── Sidebar ── */}
        <Box sx={{
          width: printMode ? '58mm' : 216, minWidth: printMode ? '58mm' : 216, flexShrink: 0,
          ...(printMode && { bgcolor: '#f0f0f0' }),
          p: printMode ? '9mm 7mm 5mm 9mm' : '40px 20px 30px',
          display: 'flex', flexDirection: 'column',
          borderRight: printMode ? 'none' : '1px solid #ddd',
        }}>
          <Box sx={{ mb: 3 }}>
            {logos.logo ? (
              <Box component="img" src={logos.logo}
                sx={{ maxWidth: printMode ? '29mm' : 115, maxHeight: printMode ? '14mm' : 44, objectFit: 'contain', display: 'block' }} />
            ) : (
              <Typography sx={{ fontWeight: 900, fontSize: '17pt', color: '#1a1a1a', letterSpacing: -1, lineHeight: 1 }}>
                tikab
              </Typography>
            )}
          </Box>

          {[
            { label: 'DATUM', value: today },
            { label: 'OFFERTNUMMER', value: meta.offerNumber || '–' },
            { label: 'VÅR PART', value: [logos.name || 'Tikab Strukturmekanik AB', meta.ourContact, meta.ourEmail, meta.ourPhone].filter(Boolean).join('\n') || '–' },
            { label: 'ER PART', value: [company?.name, meta.clientContact, meta.clientEmail, meta.clientPhone].filter(Boolean).join('\n') || '–' },
          ].map((item, i) => (
            <Box key={i} sx={{ mb: 2.5 }}>
              <Typography sx={{ fontSize: '7pt', fontWeight: 900, color: '#FF6B00', letterSpacing: 0.8, textTransform: 'uppercase', mb: 0.4 }}>
                {item.label}
              </Typography>
              <Typography sx={{ fontSize: '9pt', color: '#333', whiteSpace: 'pre-line', lineHeight: 1.4 }}>
                {item.value}
              </Typography>
            </Box>
          ))}

          <Box sx={{ mt: 2 }}>
            {logos.certLogo ? (
              <Box component="img" src={logos.certLogo}
                sx={{ width: 80, maxHeight: 60, objectFit: 'contain', display: 'block' }} />
            ) : (
              <Box sx={{ width: 60, height: 40, border: '1px dashed #ccc',
                display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 1 }}>
                <Typography sx={{ fontSize: '7pt', color: '#bbb', textAlign: 'center' }}>CERT­LOGO</Typography>
              </Box>
            )}
          </Box>

          <Box sx={{ width: 4, height: 4, borderRadius: '50%', bgcolor: '#1a1a1a', mt: 'auto' }} />
        </Box>

        {/* ── Main content ── */}
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'hidden', p: printMode ? '5mm 20mm 5mm 10mm' : '40px 45px 60px', ...(printMode && { bgcolor: 'white', minHeight: '297mm' }) }}>
          <Typography sx={{ fontWeight: 900, fontSize: '31pt', color: '#FF6B00', lineHeight: 1, mb: 3, letterSpacing: -1 }}>
            OFFERT
          </Typography>
          <Typography sx={{ fontWeight: 700, fontSize: '19pt', color: '#1a1a1a', mb: meta.subtitle ? 0.3 : 1.5, lineHeight: 1.2 }}>
            {meta.title || 'Offerttitel'}
          </Typography>
          {meta.subtitle && (
            <Typography sx={{ fontWeight: 600, fontSize: '14pt', color: '#FF6B00', mb: 1.5 }}>
              {meta.subtitle}
            </Typography>
          )}

          {meta.introText && (
            <Box sx={{ mb: 3 }}>
              {meta.introText.split('\n').reduce((acc, line, i, arr) => {
                if (line.startsWith('•')) {
                  acc.push(
                    <Box key={i} sx={{ display: 'flex', gap: 0.8, mb: 0.2 }}>
                      <Typography sx={{ fontSize: '9pt', color: '#FF6B00', lineHeight: 1.4, flexShrink: 0 }}>•</Typography>
                      <Typography sx={{ fontSize: '9pt', color: '#333', lineHeight: 1.4 }}>{line.replace(/^•\s*/, '')}</Typography>
                    </Box>
                  );
                } else if (line.trim() === '') {
                  acc.push(<Box key={i} sx={{ mb: 0.8 }} />);
                } else {
                  const isHeading = line.length < 80 && !line.endsWith('.') && !line.endsWith(',') &&
                    i < arr.length - 1 && arr[i + 1].trim() !== '' && !arr[i + 1].startsWith('•');
                  acc.push(
                    <Typography key={i} sx={{
                      fontSize: isHeading ? '10pt' : '9pt',
                      fontWeight: isHeading ? 700 : 400,
                      color: isHeading ? '#1a1a1a' : '#333',
                      lineHeight: 1.4, mb: isHeading ? 0.2 : 0,
                    }}>
                      {line}
                    </Typography>
                  );
                }
                return acc;
              }, [])}
            </Box>
          )}

          {sections.map((sec, idx) => {
            if (sec.type === 'spacer') return (
              <Box key={sec.id} sx={printMode
                ? { height: `${sec.height || 50}mm` }
                : { height: `${(sec.height || 50) * 3.78}px`, border: '1px dashed rgba(156,39,176,0.25)', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }
              }>
                {!printMode && (
                  <Typography sx={{ fontSize: '7.5pt', color: 'rgba(156,39,176,0.5)', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    utrymme {sec.height || 50} mm
                  </Typography>
                )}
              </Box>
            );
            if (sec.type === 'pagebreak') return (
              <Box key={sec.id} sx={{
                '@media print': { breakBefore: 'page' },
                ...(!printMode && { display: 'flex', alignItems: 'center', gap: 1, mb: 2, mx: -1 }),
                ...(printMode && { display: 'none' }),
              }}>
                {!printMode && <>
                  <Box sx={{ flex: 1, borderTop: '2px dashed #FF6B00', opacity: 0.4 }} />
                  <Typography sx={{ fontSize: '7.5pt', color: '#FF6B00', opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0 }}>
                    ✂ sidbrytning
                  </Typography>
                  <Box sx={{ flex: 1, borderTop: '2px dashed #FF6B00', opacity: 0.4 }} />
                </>}
              </Box>
            );
            return (
              <Box key={sec.id} sx={{ mb: 3, ...(sec.pageBreakBefore && { '@media print': { breakBefore: 'page' } }) }}>
                {sec.pageBreakBefore && !printMode && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5, mx: -1 }}>
                    <Box sx={{ flex: 1, borderTop: '2px dashed #FF6B00', opacity: 0.4 }} />
                    <Typography sx={{ fontSize: '7.5pt', color: '#FF6B00', opacity: 0.7, textTransform: 'uppercase', letterSpacing: 0.5, display: 'flex', alignItems: 'center', gap: 0.3, flexShrink: 0 }}>
                      ✂ sidbrytning
                    </Typography>
                    <Box sx={{ flex: 1, borderTop: '2px dashed #FF6B00', opacity: 0.4 }} />
                  </Box>
                )}
                <Typography sx={{ fontWeight: 700, fontSize: '10pt', color: '#FF6B00', mb: 1, letterSpacing: 0.3 }}>
                  {String(idx + 1).padStart(2, '0')} | {sec.title.toUpperCase()}
                </Typography>
                {sec.type === 'pricing' ? (
                  <PricingPreview section={sec} />
                ) : sec.type === 'team' ? (
                  <TeamPreview section={sec} users={users} />
                ) : sec.type === 'budget' ? (
                  <BudgetPreview section={sec} budgets={budgets} />
                ) : (
                  <>
                    {sec.content && (
                      <Box sx={{ mb: (sec.images || []).length > 0 ? 1.5 : 0 }}>
                        {renderContent(sec.content)}
                      </Box>
                    )}
                    {(sec.images || []).length > 0 && (
                      <Box sx={{ display: 'grid', gridTemplateColumns: sec.imageLayout === '1col' ? '1fr' : '1fr 1fr', gap: '8px' }}>
                        {sec.images.map(img => (
                          <Box key={img.id} component="img" src={img.url}
                            sx={{ width: '100%', display: 'block', borderRadius: '2px' }} />
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Box>
            );
          })}

          {/* Referensuppdrag */}
          {(() => {
            const selectedRefs = companyRefs.filter(r => selectedRefIds.includes(r.id));
            if (!selectedRefs.length) return null;
            const refIdx = sections.length + 1;
            return (
              <Box sx={{ mb: 3 }}>
                <Typography sx={{ fontWeight: 700, fontSize: '10pt', color: '#FF6B00', mb: 0.5, letterSpacing: 0.3 }}>
                  {String(refIdx).padStart(2, '0')} | REFERENSUPPDRAG
                </Typography>
                <Typography sx={{ fontSize: '9pt', color: '#888', fontStyle: 'italic', letterSpacing: 0.3, mb: 1.5 }}>
                  SE BIFOGADE REFERENSBLAD FÖR MER INFO
                </Typography>
                {selectedRefs.map((ref, i) => (
                  <Box key={ref.id} sx={{ display: 'flex', gap: 1.5, mb: 2, pb: 1.5, borderBottom: i < selectedRefs.length - 1 ? '1px solid #eee' : 'none' }}>
                    {ref.image ? (
                      <Box component="img" src={ref.image} sx={{ width: 80, height: 64, flexShrink: 0, objectFit: 'cover', borderRadius: '3px' }} />
                    ) : (
                      <Box sx={{ width: 80, height: 64, flexShrink: 0, bgcolor: '#f0f0f0', borderRadius: '3px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Typography sx={{ fontSize: '7pt', color: '#bbb', textAlign: 'center' }}>BILD</Typography>
                      </Box>
                    )}
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.3 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '10pt', color: '#1a1a1a' }}>{ref.name}</Typography>
                        {ref.projectNumber && <Typography sx={{ fontSize: '8.5pt', color: '#999' }}>#{ref.projectNumber}</Typography>}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 2, mb: 0.4 }}>
                        {ref.client && <Typography sx={{ fontSize: '9pt', color: '#555' }}>{ref.client}</Typography>}
                        {(ref.startDate || ref.endDate) && (
                          <Typography sx={{ fontSize: '9pt', color: '#888' }}>
                            {ref.startDate}{ref.endDate ? ' – ' + ref.endDate : ''}
                          </Typography>
                        )}
                      </Box>
                      {ref.projectDescription && <Typography sx={{ fontSize: '9pt', color: '#333', lineHeight: 1.4, mb: 0.3 }}>{ref.projectDescription}</Typography>}
                      {ref.ourAssignment && (
                        <Typography sx={{ fontSize: '9pt', color: '#555', lineHeight: 1.4 }}>
                          <strong>Vår uppgift: </strong>{ref.ourAssignment}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                ))}
              </Box>
            );
          })()}
        </Box>
      </Box>

      {/* Screen footer */}
      {!printMode && (
        <Box sx={{ borderTop: '1px solid #ddd', py: 1, px: 3, bgcolor: '#f9f9f9' }}>
          <Typography sx={{ fontSize: '9pt', color: '#888', textAlign: 'center' }}>
            Tikab Strukturmekanik AB | Björnstigen 87 | 170 73 Solna |
            +46 8 409 043 00 | info@tikab.com | Org.nr: 556246-6275
          </Typography>
        </Box>
      )}
    </Box>
  );
}
